import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useSeeds, useSeed } from '../hooks/useSeeds'
import { SearchableSelect } from '../components/SearchableSelect'
import { TTLPicker } from '../components/TTLPicker'
import { WorkspaceEditor } from '../components/WorkspaceEditor'
import { LongRunningOpDialog, type Interpretation } from '../components/LongRunningOpDialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import type { SeedVersion, Workspace } from '../types'

export function CreateSharePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { seeds } = useSeeds()

  const emailParam = searchParams.get('email')
  const [seed, setSeed] = useState('')
  const [workspaces, setWorkspaces] = useState<Workspace[]>([
    {
      name: 'Default',
      members: emailParam ? [{ recipient: emailParam, kind: 'email' }] : [],
      apps: null,
    },
  ])

  const [version, setVersion] = useState<string>('latest')
  const [versions, setVersions] = useState<SeedVersion[]>([])
  const [locale, setLocale] = useState<string | null>(null)
  const [ttl, setTtl] = useState<{ preset?: string; custom?: string }>({ preset: '2w' })
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  // Streamed-create progress dialog state.
  const [progressOpen, setProgressOpen] = useState(false)
  const [payload, setPayload] = useState<string | null>(null)
  const createdId = useRef<number | null>(null)

  const { seed: selectedSeedDetail } = useSeed(seed || '')
  // Apps come from the version the seed actually SERVES (`effectiveVersion`), which is
  // what the API resolves them from too.
  const allSeedApps = (selectedSeedDetail?.apps || []).filter(a => a !== 'worker')
  const noServableVersion = !!seed && !!selectedSeedDetail && !selectedSeedDetail.effectiveVersion

  useEffect(() => {
    if (!seed) { setVersions([]); setVersion('latest'); setLocale(null); return }
    let cancelled = false
    async function fetchVersions() {
      try {
        const res = await fetch(`/api/seeds/${seed}/versions`)
        if (!res.ok) return
        const data = await res.json()
        if (cancelled) return
        const allVersions: SeedVersion[] = data.versions || []
        setVersions(allVersions)
        setVersion('latest')
      } catch { /* ignore */ }
    }
    fetchVersions()
    return () => { cancelled = true }
  }, [seed])

  const deployedVersions = versions.filter(v => v.deployed || v.isCurrent)
  const selectedVersion = version === 'latest'
    ? versions.find(v => v.isLatest) ?? deployedVersions[0]
    : versions.find(v => v.version === version)
  const seedLocales = selectedVersion?.locales || []

  useEffect(() => {
    if (!seed || !selectedVersion) { setLocale(null); return }
    setLocale(current => current === null || selectedVersion.locales.includes(current)
      ? current
      : null)
  }, [seed, selectedVersion?.version, selectedVersion?.defaultLocale, selectedVersion?.locales.join('|')])

  const totalMembers = workspaces.reduce((n, w) => n + w.members.length, 0)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!seed) {
      setError('Please select a seed')
      return
    }
    if (workspaces.length === 0 || totalMembers === 0) {
      setError('Please add at least one workspace with members')
      return
    }
    for (const w of workspaces) {
      if (!w.name.trim()) {
        setError('Every workspace needs a name')
        return
      }
    }
    // A share pinned to "latest" on a seed with nothing servable can never be
    // materialized — the API refuses it with 409. Say so here rather than making the
    // user submit to find out.
    if (noServableVersion && (!version || version === 'latest')) {
      setError(`"${seed}" has no deployed, released version yet — deploy the seed before creating a share.`)
      return
    }

    setError('')
    createdId.current = null
    setPayload(JSON.stringify({
      seed,
      workspaces,
      version: version || undefined,
      locale,
      ttlPreset: ttl.preset as '1w' | '2w' | '1m' | undefined,
      customExpiresAt: ttl.custom ? new Date(ttl.custom).toISOString() : undefined,
      note: note || undefined,
    }))
    setProgressOpen(true)
  }

  // Translate the streamed-create NDJSON into the generic progress dialog's shape.
  function interpretCreateEvent(ev: unknown): Interpretation {
    const e = ev as { event: string; shareId?: number; done?: number; total?: number; phase?: string; message?: string }
    switch (e.event) {
      case 'share_created':
        if (typeof e.shareId === 'number') createdId.current = e.shareId
        return { current: 'Creating share…', log: { kind: 'info', text: 'Share record created' } }
      case 'copy_progress':
        return { current: `Copying files… (${e.done}/${e.total})`, progress: { done: e.done ?? 0, total: e.total ?? 0 } }
      case 'warm':
        return { current: 'Warming up the app…', log: { kind: 'info', text: 'Warming up the seed app…' } }
      case 'done':
        if (typeof e.shareId === 'number') createdId.current = e.shareId
        return { terminal: { kind: 'done', summary: 'Share created.' } }
      case 'error':
        return { terminal: { kind: 'error', phase: e.phase ?? 'create', message: e.message ?? 'Failed to create share' } }
      default:
        return {}
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/shares">
            <ArrowLeft />
            Back to Shares
          </Link>
        </Button>
      </div>

      <h1 className="text-2xl font-bold text-foreground mb-8">Create Share</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label>Seed</Label>
          <SearchableSelect
            options={seeds.map(s => ({ value: s.name, label: s.displayName }))}
            value={seed}
            onChange={setSeed}
            placeholder="Select a seed..."
          />
        </div>

        {seed && deployedVersions.length > 0 && (
          <div className="space-y-2">
            <Label>Version</Label>
            <select
              value={version}
              onChange={e => setVersion(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="latest">Latest (always current)</option>
              {deployedVersions.map(v => (
                <option key={v.version} value={v.version}>
                  v{v.version}{v.isCurrent ? ' (current)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {seed && seedLocales.length > 1 && (
          <div className="space-y-2">
            <Label>Default content language</Label>
            <p className="text-xs text-muted-foreground">
              Used when the visitor has not selected a supported language.
            </p>
            <select
              value={locale || ''}
              onChange={e => setLocale(e.target.value || null)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">Automatic — use visitor language</option>
              {seedLocales.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        )}

        {/* Workspaces */}
        <div className="space-y-2">
          <Label>Workspaces</Label>
          <p className="text-xs text-muted-foreground">
            A workspace is an isolated audience with its own discussion. Members of workspace A
            cannot see workspace B's messages. Add multiple workspaces to keep audiences apart
            on the same share config.
          </p>
          <WorkspaceEditor
            workspaces={workspaces}
            onChange={setWorkspaces}
            availableApps={allSeedApps}
            appsConfig={selectedSeedDetail?.appsConfig}
            restrictedApps={selectedVersion?.restrictedApps}
            seedDisplayName={selectedSeedDetail?.displayName}
          />
        </div>

        <TTLPicker value={ttl} onChange={setTtl} />

        <div className="space-y-2">
          {/* Stored as meta.note (legacy field name); surfaced to clients as the share's description. */}
          <Label>Description (optional)</Label>
          <Textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Free-text description shown to recipients (e.g., 'v2 catalog with Q3 pricing for Nestlé pitch')"
            rows={3}
          />
        </div>

        {seed && totalMembers > 0 && (
          <Card className="bg-muted/50">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium text-foreground mb-2">Review</h3>
              <p className="text-xs text-muted-foreground mb-2">
                Share will be created as a draft. You can publish it from the share detail page.
              </p>
              <dl className="space-y-1 text-sm">
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-20">Seed:</dt>
                  <dd className="text-foreground font-medium">{seed}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-20">Version:</dt>
                  <dd className="text-foreground font-mono">
                    {version === 'latest' ? 'Latest' : `v${version}`}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-20">Workspaces:</dt>
                  <dd className="text-foreground space-y-0.5">
                    {workspaces.map((w, i) => (
                      <div key={i} className="text-xs">
                        <span className="font-medium">{w.name}</span>
                        <span className="text-muted-foreground ml-2">
                          {w.members.length} member{w.members.length === 1 ? '' : 's'}
                          {' · '}{w.apps === null ? 'all apps' : `${w.apps.length} apps`}
                        </span>
                      </div>
                    ))}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-32">Default content language:</dt>
                  <dd className="text-foreground font-mono">{locale || 'Automatic'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-20">Expires:</dt>
                  <dd className="text-foreground">
                    {ttl.custom
                      ? new Date(ttl.custom).toLocaleDateString()
                      : ttl.preset === '1w' ? '1 week'
                      : ttl.preset === '1m' ? '1 month'
                      : '2 weeks'}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex gap-3">
          <Button type="submit">Create Share</Button>
          <Button variant="outline" asChild>
            <Link to="/shares">Cancel</Link>
          </Button>
        </div>
      </form>

      {payload && (
        <LongRunningOpDialog
          open={progressOpen}
          autoStart
          title="Create share"
          description="Provisioning the share and copying its files…"
          confirmLabel="Create"
          request={{ method: 'POST', url: '/api/shares?stream=1', body: payload }}
          interpretEvent={interpretCreateEvent}
          onComplete={() => { if (createdId.current != null) navigate(`/shares/${createdId.current}`) }}
          onClose={() => setProgressOpen(false)}
        />
      )}
    </div>
  )
}
